import axios from 'axios';
import PubSub from 'pubsub-js';

import { AppEvent } from 'types/AppEvent';

import { DefaultApiResponse } from '../_types';
import {
  flattenFieldErrors,
  parseChangePasswordForBE,
  parseLoginByEmailForBE,
  parseLoginByIdentifierForBE,
  parseLoginResultForUI,
  parseMeForUI,
  parsePasswordResetConfirmForBE,
  parsePasswordResetRequestForBE,
  parsePermanentPasswordForBE,
  parseMeForUpdateBE,
  parsePartialMeForUI,
  parseRegisterForBE,
  parseSocialTokenForBE,
  parseUserChoicesForUI,
} from './users.parsers';
import {
  AccountSetupChoices,
  ChangePassword,
  LoginByEmail,
  LoginByIdentifier,
  LoginResult,
  LoginResultDto,
  Me,
  MeDto,
  PasswordResetConfirm,
  PasswordResetRequest,
  PermanentPassword,
  Register,
  SocialToken,
  User,
  UserChoices,
  UserChoicesDto,
  UserDto,
} from './users.types';

const endpoints = {
  me: 'users/me/',
  updateMe: 'users/update_me/',
  loginByEmail: 'users/token/obtain2',
  loginByIdentifier: 'users/token/obtain/identifier',
  socialToken: 'users/token/social',
  permanentPassword: 'users/set_permanent_password/',
  register: 'users/register/',
  resendEmail: 'users/register/resend_email/',
  passwordReset: 'users/password_reset/',
  passwordResetConfirm: 'users/password_reset/confirm/',
  deleteAccount: 'users/delete_account/',
  changePassword: 'users/change_password/',
  accountTypeChoices: 'users/choices/account_type/',
  ageRangeChoices: 'users/choices/age_range/',
  classroomChoices: 'users/choices/classroom/',
};

type LoginErrorMessage = { message: string };

type GetMeQuery = () => Promise<Me | null>;

type LoginByEmailMutation = (data: LoginByEmail) => Promise<LoginResult | LoginErrorMessage>;

type LoginByIdentifierMutation = (data: LoginByIdentifier) => Promise<LoginResult | LoginErrorMessage>;

type SetPermanentPasswordMutation = (data: PermanentPassword) => Promise<unknown>;

type UpdateMe = (data: Partial<User>) => Promise<unknown>;

type GetUserChoicesQuery = () => Promise<UserChoices>;

type GetAccountSetupChoicesQuery = () => Promise<AccountSetupChoices>;

type RegisterMutation = (data: Register) => Promise<LoginResult>;

type SocialTokenMutation = (data: SocialToken) => Promise<LoginResult>;

type PasswordResetMutation = (data: PasswordResetRequest) => Promise<void>;

type PasswordResetConfirmMutation = (data: PasswordResetConfirm) => Promise<void>;

type ResendEmailMutation = (email: string) => Promise<void>;

type DeleteAccountMutation = (message?: string) => Promise<void>;

type ChangePasswordMutation = (data: ChangePassword) => Promise<{ success: true } | { success: false; message: string }>;

const getUserChoices =
  (endpoint: string): GetUserChoicesQuery =>
  async () => {
    const response = await axios.get<GetUserChoicesQuery, DefaultApiResponse<UserChoicesDto>>(endpoint);
    return parseUserChoicesForUI(response.data);
  };

const loginResponseHandler = (error: unknown): LoginErrorMessage => {
  const result = { message: 'Wystąpił nieznany błąd' };
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    const messageFromApi = error.response.data.detail;
    result.message = messageFromApi || 'Taki użytkownik nie istnieje lub hasło jest niepoprawne';
  }
  return result;
};

const isExpiredSessionStatus = (status: number | undefined): boolean => status === 401 || status === 403;

const usersApi = {
  getMe: (): GetMeQuery => async () => {
    try {
      const response = await axios.get<GetMeQuery, DefaultApiResponse<MeDto>>(endpoints.me);
      return parseMeForUI(response.data);
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && isExpiredSessionStatus(error.response?.status)) {
        PubSub.publish(AppEvent.OUTDATED_TOKEN);
      }
      throw error;
    }
  },
  loginByEmail: (): LoginByEmailMutation => async data => {
    try {
      const response = await axios.post<LoginByEmailMutation, DefaultApiResponse<LoginResultDto>>(
        endpoints.loginByEmail,
        parseLoginByEmailForBE(data),
      );
      return parseLoginResultForUI(response.data);
    } catch (error) {
      return loginResponseHandler(error);
    }
  },
  loginByIdentifier: (): LoginByIdentifierMutation => async data => {
    try {
      const response = await axios.post<LoginByIdentifierMutation, DefaultApiResponse<LoginResultDto>>(
        endpoints.loginByIdentifier,
        parseLoginByIdentifierForBE(data),
      );
      return parseLoginResultForUI(response.data);
    } catch (error) {
      return loginResponseHandler(error);
    }
  },
  socialToken: (): SocialTokenMutation => async data => {
    const response = await axios.post<SocialTokenMutation, DefaultApiResponse<LoginResultDto>>(
      endpoints.socialToken,
      parseSocialTokenForBE(data),
    );
    return parseLoginResultForUI(response.data);
  },
  register: (): RegisterMutation => async data => {
    const response = await axios.post<RegisterMutation, DefaultApiResponse<LoginResultDto>>(endpoints.register, parseRegisterForBE(data));
    return parseLoginResultForUI(response.data);
  },
  passwordReset: (): PasswordResetMutation => async data => {
    await axios.post(endpoints.passwordReset, parsePasswordResetRequestForBE(data));
  },
  passwordResetConfirm: (): PasswordResetConfirmMutation => async data => {
    await axios.post(endpoints.passwordResetConfirm, parsePasswordResetConfirmForBE(data));
  },
  resendEmail: (): ResendEmailMutation => async email => {
    await axios.post(endpoints.resendEmail, { email });
  },
  deleteAccount: (): DeleteAccountMutation => async message => {
    await axios.post(endpoints.deleteAccount, { message: message || undefined });
  },
  changePassword: (): ChangePasswordMutation => async data => {
    try {
      await axios.post(endpoints.changePassword, parseChangePasswordForBE(data));
      return { success: true };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        return { success: false, message: flattenFieldErrors(error.response.data) };
      }
      return { success: false, message: 'Wystąpił nieznany błąd' };
    }
  },
  setPermanentPassword: (): SetPermanentPasswordMutation => async formData => {
    const data = parsePermanentPasswordForBE(formData);
    return axios.post(endpoints.permanentPassword, data);
  },
  updateMe: (): UpdateMe => async formData => {
    const data = parseMeForUpdateBE(formData);
    const response = await axios.patch<Partial<UserDto>>(endpoints.updateMe, data);
    return parsePartialMeForUI(response.data);
  },
  getAccountTypeChoices: (): GetUserChoicesQuery => getUserChoices(endpoints.accountTypeChoices),
  getAgeRangeChoices: (): GetUserChoicesQuery => getUserChoices(endpoints.ageRangeChoices),
  getClassroomChoices: (): GetUserChoicesQuery => getUserChoices(endpoints.classroomChoices),
  markSeenGuides: async (seenGuides: string[]) => {
    await axios.patch(endpoints.updateMe, { seen_guides: seenGuides });
  },
  getAccountSetupChoices: (): GetAccountSetupChoicesQuery => async () => {
    const [accountTypeChoices, ageRangeChoices, classChoices] = await Promise.all([
      getUserChoices(endpoints.accountTypeChoices)(),
      getUserChoices(endpoints.ageRangeChoices)(),
      getUserChoices(endpoints.classroomChoices)(),
    ]);
    return { accountTypeChoices, ageRangeChoices, classChoices };
  },
};

export default usersApi;
